import React, { useState, useEffect, useCallback } from 'react'
import {
  Globe, Server, ShieldCheck, ShieldAlert, AlertTriangle, CheckCircle,
  Clock, RefreshCw, Plus, Trash2, Activity, Terminal, ExternalLink,
  ChevronDown, ChevronUp, Layers, HardDrive
} from 'lucide-react'
import {
  getApplications, createApplication, deleteApplication,
  probeApplication, getApplicationHistory, getClients
} from '../api'

export default function ApplicationHealthMap() {
  const [endpoints, setEndpoints] = useState([])
  const [summary, setSummary] = useState({ up: 0, degraded: 0, down: 0, total: 0 })
  const [clients, setClients] = useState([])
  const [selectedClient, setSelectedClient] = useState('')
  const [loading, setLoading] = useState(true)
  const [probingId, setProbingId] = useState(null)
  const [historyModal, setHistoryModal] = useState(null)
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [expandedId, setExpandedId] = useState(null)

  // Form state
  const [newClient, setNewClient] = useState('')
  const [newName, setNewName] = useState('')
  const [newUrl, setNewUrl] = useState('')
  const [newHost, setNewHost] = useState('')
  const [newDaemon, setNewDaemon] = useState('')
  const [newExpectedStatus, setNewExpectedStatus] = useState(200)
  const [newTimeout, setNewTimeout] = useState(10)
  const [formError, setFormError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const [appData, clientData] = await Promise.all([
        getApplications(selectedClient || null),
        getClients()
      ])
      setEndpoints(appData?.endpoints || [])
      setSummary(appData?.summary || { up: 0, degraded: 0, down: 0, total: 0 })
      setClients(clientData?.clients || [])
      if (!newClient && clientData?.clients?.length > 0) {
        setNewClient(clientData.clients[0].id)
      }
    } catch {
      setEndpoints([])
    } finally {
      setLoading(false)
    }
  }, [selectedClient, newClient])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleProbeNow = async (endpointId) => {
    setProbingId(endpointId)
    try {
      await probeApplication(endpointId)
      await fetchData()
    } catch (err) {
      alert(`Probe failed: ${err.message}`)
    } finally {
      setProbingId(null)
    }
  }

  const handleDelete = async (endpointId, name) => {
    if (!window.confirm(`Are you sure you want to stop monitoring ${name}?`)) return
    try {
      await deleteApplication(endpointId)
      await fetchData()
    } catch (err) {
      alert(`Deletion failed: ${err.message}`)
    }
  }

  const handleViewHistory = async (endpoint) => {
    try {
      const hist = await getApplicationHistory(endpoint.id, 30)
      setHistoryModal({ endpoint, history: hist.probes || [] })
    } catch (err) {
      alert(`Could not load history: ${err.message}`)
    }
  }

  const handleCreateSubmit = async (e) => {
    e.preventDefault()
    setFormError('')
    if (!newClient || !newName.trim() || !newUrl.trim()) {
      setFormError('Client, Application Name, and Public URL are required.')
      return
    }
    setSubmitting(true)
    try {
      await createApplication({
        client_id: parseInt(newClient, 10),
        name: newName.trim(),
        public_url: newUrl.trim(),
        host_server_id: newHost.trim() || null,
        backend_daemon: newDaemon.trim() || null,
        expected_status: parseInt(newExpectedStatus, 10) || 200,
        timeout_seconds: parseInt(newTimeout, 10) || 10,
        check_interval_seconds: 60,
        is_active: true
      })
      setCreateModalOpen(false)
      setNewName('')
      setNewUrl('')
      setNewHost('')
      setNewDaemon('')
      await fetchData()
    } catch (err) {
      setFormError(err.message || 'Failed to register endpoint.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="animate-in" style={{ maxWidth: 1280, margin: '0 auto' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 24,
        flexWrap: 'wrap',
        gap: 16
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              background: 'var(--brand-teal-bg)',
              padding: 7,
              borderRadius: 8,
              border: '1.5px solid var(--border-ink)',
              boxShadow: 'var(--shadow-solid-sm)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--brand-teal)'
            }}>
              <Globe size={20} />
            </div>
            <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: 22, fontWeight: 800, margin: 0, color: 'var(--ink-900)' }}>
              App Topology Map &amp; 4-Layer Service Health
            </h2>
          </div>
          <p style={{ margin: '4px 0 0', color: 'var(--ink-600)', fontSize: 13 }}>
            Continuous 60s multi-layer diagnosis: SSL Handshake, HTTP Latency, Reverse Proxy 502 Upstream, and Backend Daemon correlation.
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <select
            value={selectedClient}
            onChange={(e) => setSelectedClient(e.target.value)}
            className="input-select"
            style={{
              padding: '7px 12px',
              borderRadius: 8,
              border: '1.5px solid var(--border-ink)',
              fontSize: 13,
              fontWeight: 600,
              background: '#FFFFFF',
              boxShadow: 'var(--shadow-solid-sm)',
              color: 'var(--ink-900)'
            }}
          >
            <option value="">All Monitored Clients</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>{c.client_name}</option>
            ))}
          </select>

          <button
            type="button"
            onClick={() => setCreateModalOpen(true)}
            className="btn btn-primary btn-sm"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              border: '2px solid var(--border-ink)',
              boxShadow: 'var(--shadow-solid-sm)',
              borderRadius: 8,
              fontWeight: 700
            }}
          >
            <Plus size={15} /> Register Application
          </button>

          <button
            type="button"
            onClick={fetchData}
            className="btn btn-secondary btn-sm"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              border: '1.5px solid var(--border-ink)',
              boxShadow: 'var(--shadow-solid-sm)',
              borderRadius: 8,
              fontWeight: 700
            }}
          >
            <RefreshCw size={14} className={loading ? 'spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 16,
        marginBottom: 24
      }}>
        <div style={{
          background: '#FFFFFF',
          padding: '16px 20px',
          borderRadius: 12,
          border: '2px solid var(--border-ink)',
          borderLeft: '6px solid var(--border-ink)',
          boxShadow: 'var(--shadow-solid-sm)'
        }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--ink-600)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Total Monitored Apps
          </div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 26, fontWeight: 800, color: 'var(--ink-900)', marginTop: 4 }}>
            {summary.total}
          </div>
        </div>

        <div style={{
          background: '#FFFFFF',
          padding: '16px 20px',
          borderRadius: 12,
          border: '2px solid var(--border-ink)',
          borderLeft: '6px solid #10B981',
          boxShadow: 'var(--shadow-solid-sm)'
        }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: '#047857', display: 'flex', alignItems: 'center', gap: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            <CheckCircle size={14} /> Healthy (UP)
          </div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 26, fontWeight: 800, color: '#047857', marginTop: 4 }}>
            {summary.up}
          </div>
        </div>

        <div style={{
          background: '#FFFFFF',
          padding: '16px 20px',
          borderRadius: 12,
          border: '2px solid var(--border-ink)',
          borderLeft: '6px solid #D97706',
          boxShadow: 'var(--shadow-solid-sm)'
        }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: '#B45309', display: 'flex', alignItems: 'center', gap: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            <AlertTriangle size={14} /> Degraded
          </div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 26, fontWeight: 800, color: '#B45309', marginTop: 4 }}>
            {summary.degraded}
          </div>
        </div>

        <div style={{
          background: '#FFFFFF',
          padding: '16px 20px',
          borderRadius: 12,
          border: '2px solid var(--border-ink)',
          borderLeft: '6px solid var(--brand-coral)',
          boxShadow: 'var(--shadow-solid-sm)'
        }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--brand-coral)', display: 'flex', alignItems: 'center', gap: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            <ShieldAlert size={14} /> Service DOWN
          </div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 26, fontWeight: 800, color: 'var(--brand-coral)', marginTop: 4 }}>
            {summary.down}
          </div>
        </div>
      </div>

      {/* Main Endpoints List */}
      <div style={{
        background: '#FFFFFF',
        borderRadius: 12,
        border: '2px solid var(--border-ink)',
        boxShadow: 'var(--shadow-solid-md)',
        overflow: 'hidden'
      }}>
        <div style={{
          padding: '16px 20px',
          borderBottom: '2px solid var(--border-ink)',
          background: 'var(--canvas-subtle)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h3 style={{ margin: 0, fontFamily: 'var(--font-serif)', fontSize: 15, fontWeight: 800, color: 'var(--ink-900)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Layers size={18} /> Active Service Endpoints &amp; Root-Cause Diagnostics
          </h3>
          <span style={{ fontSize: 12, color: 'var(--ink-600)', fontFamily: 'var(--font-mono)' }}>Probing every 60s</span>
        </div>

        {endpoints.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: 'var(--ink-600)' }}>
            <div style={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              background: 'var(--canvas-subtle)',
              border: '1.5px solid var(--border-ink)',
              color: 'var(--ink-500)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 14px'
            }}>
              <Globe size={24} />
            </div>
            <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: 18, fontWeight: 800, margin: '0 0 6px', color: 'var(--ink-900)' }}>
              No application endpoints registered yet.
            </h3>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--ink-600)' }}>
              Click "Register Application" above to add your domains and services for 4-layer health checks.
            </p>
          </div>
        ) : (
          <div>
            {endpoints.map((ep) => {
              const isUp = ep.current_status === 'UP'
              const isDegraded = ep.current_status === 'DEGRADED'
              const isDown = ep.current_status === 'DOWN'
              const isExpanded = expandedId === ep.id

              const statusColor = isUp ? '#047857' : (isDegraded ? '#B45309' : 'var(--brand-coral)')
              const statusBg = isUp ? '#ECFDF5' : (isDegraded ? '#FEF3C7' : 'var(--brand-coral-bg)')
              const statusBorder = isUp ? '#10B981' : (isDegraded ? '#D97706' : 'var(--brand-coral-border)')

              return (
                <div key={ep.id} style={{
                  borderBottom: '1px solid var(--border-subtle)',
                  padding: '18px 20px',
                  background: isDown ? '#FFFBFB' : '#FFFFFF'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 14 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 10px',
                        borderRadius: 6,
                        fontSize: 11,
                        fontWeight: 800,
                        color: statusColor,
                        background: statusBg,
                        border: `1.5px solid ${statusBorder}`
                      }}>
                        ● {ep.current_status}
                      </span>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <h4 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: 'var(--ink-900)' }}>{ep.name}</h4>
                          <span style={{ fontSize: 12, color: 'var(--ink-500)' }}>({ep.client_name || 'Client'})</span>
                        </div>
                        <a
                          href={ep.public_url}
                          target="_blank"
                          rel="noreferrer"
                          style={{ fontSize: 12, color: 'var(--brand-blue)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4, marginTop: 2, fontFamily: 'var(--font-mono)' }}
                        >
                          {ep.public_url} <ExternalLink size={11} />
                        </a>
                      </div>
                    </div>

                    {/* Diagnostic Metrics */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 20, fontSize: 13, flexWrap: 'wrap' }}>
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-500)', textTransform: 'uppercase' }}>HTTP Status</div>
                        <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 800, color: isUp ? '#047857' : 'var(--brand-coral)' }}>
                          {ep.last_status_code ? `${ep.last_status_code}` : 'Unavailable'}
                        </div>
                      </div>

                      <div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-500)', textTransform: 'uppercase' }}>Latency</div>
                        <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 800, color: 'var(--ink-900)' }}>
                          {ep.last_latency_ms !== null ? `${ep.last_latency_ms} ms` : '—'}
                        </div>
                      </div>

                      <div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-500)', textTransform: 'uppercase' }}>SSL Validity</div>
                        <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 800, color: (ep.last_ssl_days_remaining !== null && ep.last_ssl_days_remaining <= 7) ? 'var(--brand-coral)' : '#047857' }}>
                          {ep.last_ssl_days_remaining !== null ? `${ep.last_ssl_days_remaining}d remaining` : 'No SSL / Pending'}
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <button
                          type="button"
                          onClick={() => handleProbeNow(ep.id)}
                          disabled={probingId === ep.id}
                          title="Force diagnostic probe now"
                          className="btn btn-secondary btn-sm"
                          style={{
                            padding: '4px 10px',
                            border: '1.5px solid var(--border-ink)',
                            boxShadow: 'var(--shadow-solid-sm)',
                            borderRadius: 6,
                            fontSize: 11,
                            fontWeight: 700
                          }}
                        >
                          <RefreshCw size={12} className={probingId === ep.id ? 'spin' : ''} />
                          {probingId === ep.id ? 'Probing…' : 'Probe'}
                        </button>

                        <button
                          type="button"
                          onClick={() => handleViewHistory(ep)}
                          title="View probe latency and outage history"
                          className="btn btn-secondary btn-sm"
                          style={{
                            padding: '4px 10px',
                            border: '1.5px solid var(--border-ink)',
                            boxShadow: 'var(--shadow-solid-sm)',
                            borderRadius: 6,
                            fontSize: 11,
                            fontWeight: 700
                          }}
                        >
                          History
                        </button>

                        <button
                          type="button"
                          onClick={() => setExpandedId(isExpanded ? null : ep.id)}
                          style={{
                            padding: '6px 8px',
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            color: 'var(--ink-600)'
                          }}
                        >
                          {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </button>

                        <button
                          type="button"
                          onClick={() => handleDelete(ep.id, ep.name)}
                          title="Delete endpoint"
                          style={{
                            padding: '6px 8px',
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            color: 'var(--brand-coral)'
                          }}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Root Cause Banner if Not UP */}
                  {!isUp && ep.last_root_cause && (
                    <div style={{
                      marginTop: 12,
                      padding: '10px 14px',
                      background: isDown ? 'var(--brand-coral-bg)' : '#FEF3C7',
                      borderLeft: `5px solid ${isDown ? 'var(--brand-coral)' : '#D97706'}`,
                      border: '1.5px solid var(--border-ink)',
                      borderRadius: 6,
                      fontSize: 13,
                      color: isDown ? 'var(--brand-coral)' : '#92400E'
                    }}>
                      <strong>Root Cause Diagnosis:</strong> {ep.last_root_cause}
                    </div>
                  )}

                  {/* Expanded 3:00 AM Operator Cheat-Sheet */}
                  {isExpanded && (
                    <div style={{
                      marginTop: 16,
                      padding: 16,
                      background: '#121826',
                      borderRadius: 8,
                      border: '2px solid var(--border-ink)',
                      color: '#F1F5F9',
                      fontSize: 12
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#38BDF8', fontWeight: 800, marginBottom: 10 }}>
                        <Terminal size={14} /> 3:00 AM Operator Incident Cheat-Sheet
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 }}>
                        <div>
                          <div style={{ color: '#94A3B8', fontSize: 11 }}>Host Server Node:</div>
                          <code style={{ background: '#1E293B', padding: '3px 8px', borderRadius: 4, color: '#38BDF8', display: 'inline-block', marginTop: 2 }}>
                            {ep.host_server_id || 'Not specified'}
                          </code>
                          <div style={{ marginTop: 8, color: '#94A3B8', fontSize: 11 }}>SSH Diagnostic String:</div>
                          <code style={{ background: '#1E293B', padding: '3px 8px', borderRadius: 4, color: '#4ADE80', display: 'block', wordBreak: 'break-all', marginTop: 2 }}>
                            ssh ubuntu@{ep.host_server_id || '<server-ip>'}
                          </code>
                        </div>

                        <div>
                          <div style={{ color: '#94A3B8', fontSize: 11 }}>Backend Application Daemon:</div>
                          <code style={{ background: '#1E293B', padding: '3px 8px', borderRadius: 4, color: '#FDE047', display: 'inline-block', marginTop: 2 }}>
                            {ep.backend_daemon || 'Not specified'}
                          </code>
                          <div style={{ marginTop: 8, color: '#94A3B8', fontSize: 11 }}>Safe Service Reload Command:</div>
                          <code style={{ background: '#1E293B', padding: '3px 8px', borderRadius: 4, color: '#4ADE80', display: 'block', marginTop: 2 }}>
                            sudo systemctl restart {ep.backend_daemon || '<service-name>'}
                          </code>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Register Endpoint Modal */}
      {createModalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(18, 24, 38, 0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
          <div style={{
            background: '#FFFFFF',
            borderRadius: 14,
            border: '2px solid var(--border-ink)',
            width: '100%',
            maxWidth: 520,
            padding: 24,
            boxShadow: 'var(--shadow-solid-lg)'
          }}>
            <h3 style={{ margin: '0 0 16px', fontFamily: 'var(--font-serif)', fontSize: 18, fontWeight: 800, color: 'var(--ink-900)' }}>
              Register Monitored Application Endpoint
            </h3>

            {formError && (
              <div style={{ padding: '10px 14px', background: 'var(--brand-coral-bg)', border: '1.5px solid var(--brand-coral-border)', color: 'var(--brand-coral)', borderRadius: 6, fontSize: 13, marginBottom: 14 }}>
                {formError}
              </div>
            )}

            <form onSubmit={handleCreateSubmit}>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--ink-700)', marginBottom: 4 }}>Target Client *</label>
                <select
                  value={newClient}
                  onChange={(e) => setNewClient(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--border-ink)', fontSize: 13, background: '#FFFFFF', color: 'var(--ink-900)' }}
                >
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>{c.client_name}</option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--ink-700)', marginBottom: 4 }}>Application Name *</label>
                <input
                  type="text"
                  placeholder="e.g. Production Web Portal"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--border-ink)', fontSize: 13, color: 'var(--ink-900)' }}
                />
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--ink-700)', marginBottom: 4 }}>Public URL *</label>
                <input
                  type="url"
                  placeholder="https://app.example.com"
                  value={newUrl}
                  onChange={(e) => setNewUrl(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--border-ink)', fontSize: 13, color: 'var(--ink-900)' }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--ink-700)', marginBottom: 4 }}>Host Server / Instance ID</label>
                  <input
                    type="text"
                    placeholder="i-06dea877d3dad7779"
                    value={newHost}
                    onChange={(e) => setNewHost(e.target.value)}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--border-ink)', fontSize: 13, color: 'var(--ink-900)' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--ink-700)', marginBottom: 4 }}>Backend Daemon</label>
                  <input
                    type="text"
                    placeholder="php8.2-fpm, uvicorn"
                    value={newDaemon}
                    onChange={(e) => setNewDaemon(e.target.value)}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--border-ink)', fontSize: 13, color: 'var(--ink-900)' }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--ink-700)', marginBottom: 4 }}>Expected HTTP Status</label>
                  <input
                    type="number"
                    value={newExpectedStatus}
                    onChange={(e) => setNewExpectedStatus(e.target.value)}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--border-ink)', fontSize: 13, color: 'var(--ink-900)' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--ink-700)', marginBottom: 4 }}>Timeout (seconds)</label>
                  <input
                    type="number"
                    value={newTimeout}
                    onChange={(e) => setNewTimeout(e.target.value)}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--border-ink)', fontSize: 13, color: 'var(--ink-900)' }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button
                  type="button"
                  onClick={() => setCreateModalOpen(false)}
                  className="btn btn-secondary"
                  style={{ border: '1.5px solid var(--border-ink)', borderRadius: 8 }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn btn-primary"
                  style={{ border: '2px solid var(--border-ink)', borderRadius: 8, boxShadow: 'var(--shadow-solid-sm)' }}
                >
                  {submitting ? 'Registering…' : 'Save & Probe Now'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* History Modal */}
      {historyModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(18, 24, 38, 0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
          <div style={{
            background: '#FFFFFF',
            borderRadius: 14,
            border: '2px solid var(--border-ink)',
            width: '100%',
            maxWidth: 720,
            maxHeight: '80vh',
            display: 'flex',
            flexDirection: 'column',
            padding: 24,
            boxShadow: 'var(--shadow-solid-lg)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div>
                <h3 style={{ margin: 0, fontFamily: 'var(--font-serif)', fontSize: 18, fontWeight: 800, color: 'var(--ink-900)' }}>
                  Probe History: {historyModal.endpoint.name}
                </h3>
                <span style={{ fontSize: 12, color: 'var(--ink-500)', fontFamily: 'var(--font-mono)' }}>{historyModal.endpoint.public_url}</span>
              </div>
              <button
                type="button"
                onClick={() => setHistoryModal(null)}
                className="btn btn-secondary btn-sm"
                style={{ border: '1.5px solid var(--border-ink)', borderRadius: 6 }}
              >
                Close
              </button>
            </div>

            <div style={{ overflowY: 'auto', flex: 1 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: 'var(--canvas-subtle)', textAlign: 'left', borderBottom: '2px solid var(--border-ink)' }}>
                    <th style={{ padding: '8px 12px', fontWeight: 800 }}>Timestamp (UTC)</th>
                    <th style={{ padding: '8px 12px', fontWeight: 800 }}>Status</th>
                    <th style={{ padding: '8px 12px', fontWeight: 800 }}>HTTP</th>
                    <th style={{ padding: '8px 12px', fontWeight: 800 }}>Latency</th>
                    <th style={{ padding: '8px 12px', fontWeight: 800 }}>Diagnosis</th>
                  </tr>
                </thead>
                <tbody style={{ fontFamily: 'var(--font-mono)' }}>
                  {historyModal.history.map((h) => (
                    <tr key={h.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                      <td style={{ padding: '8px 12px', color: 'var(--ink-600)', fontFamily: 'var(--font-sans)' }}>{h.checked_at}</td>
                      <td style={{ padding: '8px 12px' }}>
                        <span style={{
                          padding: '2px 6px',
                          borderRadius: 6,
                          fontSize: 10,
                          fontWeight: 700,
                          color: h.status === 'UP' ? '#047857' : 'var(--brand-coral)',
                          background: h.status === 'UP' ? '#ECFDF5' : 'var(--brand-coral-bg)',
                          border: `1px solid ${h.status === 'UP' ? '#10B981' : 'var(--brand-coral-border)'}`
                        }}>
                          {h.status}
                        </span>
                      </td>
                      <td style={{ padding: '8px 12px', fontWeight: 700 }}>{h.http_status || '—'}</td>
                      <td style={{ padding: '8px 12px' }}>{h.latency_ms !== null ? `${h.latency_ms} ms` : '—'}</td>
                      <td style={{ padding: '8px 12px', color: h.root_cause_diagnosis ? 'var(--brand-coral)' : 'var(--ink-500)', fontFamily: 'var(--font-sans)' }}>
                        {h.root_cause_diagnosis || 'Clean'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
